import { useEffect, useRef, useState } from 'react'

import { fetchHelpArticle } from '../service/helpApi'
import type { HelpArticleDetail, Surface } from '../types'
import { ArticleMarkdown } from './ArticleMarkdown'
import { ScreenSuggestions } from './ScreenSuggestions'

interface ReaderPaneProps {
  slug: string | null
  surface: Surface
  onOpenArticle: (slug: string) => void
}

// The right pane: the cited article, or the screen's suggested articles before an
// answer arrives.
export function ReaderPane({ slug, surface, onOpenArticle }: ReaderPaneProps) {
  const [article, setArticle] = useState<HelpArticleDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!slug) {
      setArticle(null)
      return
    }
    let active = true
    setLoading(true)
    setFailed(false)
    fetchHelpArticle(slug)
      .then((loaded) => {
        if (!active) return
        setArticle(loaded)
        setLoading(false)
        scrollRef.current?.scrollTo({ top: 0 })
      })
      .catch(() => {
        if (!active) return
        setFailed(true)
        setLoading(false)
      })
    return () => {
      active = false
    }
  }, [slug])

  return (
    <div ref={scrollRef} className="h-full overflow-y-auto bg-blue-1/40">
      {!slug && (
        <ScreenSuggestions surface={surface} onOpenArticle={onOpenArticle} />
      )}

      {slug && loading && (
        <div className="space-y-3 p-6">
          <div className="h-6 w-2/3 animate-pulse rounded bg-blue-2" />
          <div className="h-4 w-full animate-pulse rounded bg-blue-2" />
          <div className="h-4 w-11/12 animate-pulse rounded bg-blue-2" />
          <div className="h-4 w-4/5 animate-pulse rounded bg-blue-2" />
        </div>
      )}

      {slug && failed && (
        <p className="p-6 text-sm text-blue-5">
          Couldn't load that article. Open it on the help center instead.
        </p>
      )}

      {slug && article && !loading && (
        <article className="p-6">
          {article.category && (
            <p className="text-xs font-semibold uppercase tracking-wide text-brand">
              {article.category}
            </p>
          )}
          <h1 className="mb-4 mt-1 text-2xl font-semibold tracking-tight text-blue-7 text-pretty">
            {article.title}
          </h1>
          <ArticleMarkdown body={article.body} onOpenArticle={onOpenArticle} />
          <p className="mt-8 border-t border-border pt-3 text-xs text-blue-4">
            From the CounselPro help center
          </p>
        </article>
      )}
    </div>
  )
}
